import { Suspense } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Icon } from '../../components/ui/Icon';
import { cn } from '../../lib/cn';
import { useAuth, usePermissions, useSession } from '../auth/useAuth';
import { TenantAccessBanner } from './TenantAccessBanner';
import { PSpinner } from './ui';

interface Item {
  label: string;
  to: string;
  icon: string;
  show: boolean;
}

/**
 * The dark Platform Console shell (DESIGN.md's `platform-*` token table,
 * verified against all 6 reference screens' own inline Tailwind configs
 * before this phase started building — `phase-5-backend-support-matrix.md`
 * records the check). Replaces the pre-Stage-4 light shell (same `bg-canvas`
 * / `bg-primary` tokens as the Tenant Shell) that every platform route used
 * until now — the two shells share nothing visually by design (DESIGN.md:
 * "locked light Tenant Shell + dark Platform Console").
 */
export function PlatformShell() {
  const session = useSession();
  const permissions = usePermissions();
  const { logout } = useAuth();

  const items: Item[] = [
    { label: 'Console home', to: '/platform', icon: 'space_dashboard', show: true },
    { label: 'Tenant lookup', to: '/platform/lookup', icon: 'manage_search', show: permissions.can('tenant.view') },
    { label: 'Onboard tenant', to: '/platform/onboard', icon: 'add_business', show: permissions.can('tenant.create') },
    { label: 'Statutory rates', to: '/platform/statutory-rates', icon: 'account_balance', show: permissions.can('audit.view') },
    { label: 'Device codes', to: '/platform/device-codes', icon: 'developer_board', show: permissions.can('device_mappings.read') },
    { label: 'Backups', to: '/platform/backups', icon: 'backup', show: permissions.can('platform.backup.view') },
  ].filter((i) => i.show);

  return (
    <div className="flex min-h-screen bg-platform-bg text-platform-text">
      <aside className="fixed left-0 top-0 z-40 hidden h-full w-64 flex-col justify-between border-r border-platform-border bg-platform-sidebar md:flex">
        <div className="flex flex-col">
          <div className="flex h-14 items-center justify-between border-b border-platform-border px-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-tight border border-platform-border-strong bg-platform-surface font-mono text-xs font-bold tracking-wider text-platform-text">
                <Icon name="admin_panel_settings" className="text-[18px]" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-bold tracking-tight text-platform-text">MERVONN</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-platform-muted">Platform Console</span>
              </div>
            </div>
          </div>
          <nav aria-label="Platform" className="flex flex-col gap-0.5 px-3 py-3">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/platform'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-tight px-3 py-2 text-xs font-medium transition-colors',
                    isActive
                      ? 'border-l-2 border-platform-accent bg-platform-surface font-semibold text-platform-text'
                      : 'text-platform-muted hover:bg-platform-surface hover:text-platform-text',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon name={item.icon} className={cn('text-[18px]', isActive && 'text-platform-accent')} />
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="border-t border-platform-border p-3">
          <div className="flex items-center gap-2.5 rounded-tight border border-platform-border bg-platform-surface p-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-platform-border bg-platform-card-muted text-xs font-semibold text-platform-text">
              {session.display_name.slice(0, 2).toUpperCase()}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-semibold leading-tight text-platform-text">{session.display_name}</span>
              <span className="truncate font-mono text-[10px] text-platform-muted">{session.role_name}</span>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col md:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-platform-border bg-platform-sidebar px-4 sm:px-6">
          <span className="font-mono text-xs font-medium text-platform-muted">Platform console</span>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger className="flex items-center gap-2 rounded-tight px-2 py-1.5 text-sm text-platform-text hover:bg-platform-surface">
              {session.display_name}
              <Icon name="expand_more" className="text-[16px] text-platform-muted" />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align="end"
                sideOffset={6}
                className="z-50 min-w-52 rounded-tight border border-platform-border bg-platform-surface p-1 shadow-card"
              >
                <div className="px-2 py-1.5 text-xs text-platform-muted">{session.email}</div>
                <DropdownMenu.Separator className="my-1 h-px bg-platform-border" />
                <DropdownMenu.Item
                  onSelect={(e) => {
                    e.preventDefault();
                    void logout();
                  }}
                  className="cursor-pointer rounded-tight px-2 py-1.5 text-sm text-platform-text outline-none hover:bg-platform-card-muted"
                >
                  Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </header>

        <nav className="flex gap-1 overflow-x-auto border-b border-platform-border bg-platform-sidebar px-2 py-1.5 md:hidden">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/platform'}
              className={({ isActive }) =>
                cn(
                  'whitespace-nowrap rounded-tight px-3 py-1.5 text-xs font-medium',
                  isActive ? 'bg-platform-surface text-platform-text' : 'text-platform-muted',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <TenantAccessBanner />

        <main className="mx-auto w-full max-w-7xl flex-1 p-4 sm:p-8">
          <Suspense fallback={<PSpinner label="Loading…" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
